import { makeAutoObservable, runInAction } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useEffect } from 'react';
import { UserLessonListItemResponse } from '../../../server/response/listeningResponseScheme';
import firebaseStore from '../../stores/firebaseStore';
import { apiClient } from '../../clientUtils/apiClient';
import { useMobxInstance } from '../../clientUtils/mobxUtil';
import { CommonLink } from '../../views/common/CommonLink';
import { ChevronDownIcon, MoveDownIcon, MoveUpIcon } from 'lucide-react';
import _ from 'lodash';
import TextRotate from '../../views/common/TextRotate';
import { twMerge } from 'tailwind-merge';

class ViewStore {
    userLessons: UserLessonListItemResponse[] = [];
    isLoading = false;
    isLoadingMore = false;
    isEnd = false;

    constructor() {
        makeAutoObservable(this);
    }

    async fetch() {
        this.isLoading = true;

        const isLoggedIn = await firebaseStore.isLoggedIn();

        if (isLoggedIn) {
            try {
                const data = await apiClient.userLesson.member.getUserLessonList.query({
                    fromLessonHash: null,
                });
                runInAction(() => {
                    this.userLessons = data.items;
                    this.isEnd = data.isEnd;
                    this.isLoading = false;
                });
            } catch (e) {
                runInAction(() => {
                    this.userLessons = [];
                    this.isEnd = true;
                    this.isLoading = false;
                });
            }
        } else {
            runInAction(() => {
                this.userLessons = [];
                this.isEnd = true;
                this.isLoading = false;
            });
        }
    }

    async fetchMore() {
        if (this.isEnd || this.isLoadingMore) {
            return;
        }

        const lastUserLesson = this.userLessons[this.userLessons.length - 1];
        if (!lastUserLesson) {
            return;
        }

        this.isLoadingMore = true;

        try {
            const data = await apiClient.userLesson.member.getUserLessonList.query({
                fromLessonHash: lastUserLesson.lesson.hash,
            });
            runInAction(() => {
                this.userLessons = [...this.userLessons, ...data.items];
                this.isEnd = data.isEnd;
                this.isLoadingMore = false;
            });
        } catch (e) {
            runInAction(() => {
                this.isLoadingMore = false;
            });
        }
    }
}

// 레슨 아이템 컴포넌트
const LessonItem = observer(({ userLesson }: { userLesson: UserLessonListItemResponse }) => {
    // progress는 향후 진도율 연동 시 추가
    const progress = 0;

    return (
        <CommonLink
            to={`/my/lesson/${userLesson.lesson.hash}`}
            className={twMerge(
                'flex w-full items-stretch gap-4 rounded-lg text-left',
                'group', // TextRotate에서 사용하는 group 클래스
            )}
        >
            {/* 썸네일 */}
            <div className="flex min-w-40 flex-1/3 items-center justify-center">
                <div className="bg-base-300 aspect-video overflow-hidden rounded-md">
                    {userLesson.lesson.video.thumbnail && (
                        <img
                            src={userLesson.lesson.video.thumbnail}
                            alt={userLesson.lesson.titleKo || userLesson.lesson.video.title}
                            className="h-full w-full object-cover"
                        />
                    )}
                </div>
            </div>
            {/* 제목 & 요약 & 진도율 */}
            <div className="flex min-w-0 flex-2/3 flex-col justify-between gap-2 py-2">
                <div className="flex flex-col gap-1">
                    <p className="text-base-content line-clamp-1 min-h-7 sm:line-clamp-2">
                        <TextRotate
                            texts={[
                                userLesson.lesson.title || userLesson.lesson.video.title,
                                userLesson.lesson.titleKo || '',
                            ]}
                            delay={1000}
                            useParentHover={true}
                            className="text-base-content"
                        />
                    </p>
                    <p className="text-base-content/50 line-clamp-1 min-h-6 text-sm sm:line-clamp-2">
                        {userLesson.lesson.overviewKo}
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <div className="bg-base-300 h-1.5 flex-1 overflow-hidden rounded-full">
                        <div
                            className="bg-primary h-full transition-all"
                            style={{ width: `${progress}%` }}
                        />
                    </div>
                    <span className="text-base-content/50 h-6 w-8 shrink-0 text-xs">
                        {progress}%
                    </span>
                </div>
            </div>
        </CommonLink>
    );
});

// 스켈레톤 컴포넌트
const LessonItemSkeleton = () => (
    <div className="flex w-full items-stretch gap-4 rounded-lg">
        {/* 썸네일 스켈레톤 */}
        <div className="flex min-w-40 flex-1/3 items-center justify-center">
            <div className="skeleton aspect-video w-full rounded-md" />
        </div>
        {/* 콘텐츠 스켈레톤 */}
        <div className="flex min-w-0 flex-2/3 flex-col justify-between gap-2 py-2">
            <div className="flex flex-col gap-1">
                <div className="skeleton h-7 w-3/4 rounded" />
                <div className="skeleton h-6 w-full rounded" />
            </div>
            <div className="flex items-center gap-2">
                <div className="skeleton h-1.5 flex-1 rounded-full" />
                <div className="skeleton h-6 w-8 rounded" />
            </div>
        </div>
    </div>
);

const UserLessonList = observer(() => {
    const viewStore = useMobxInstance(ViewStore);

    useEffect(() => {
        viewStore.fetch();
    }, [viewStore]);

    // 로딩 상태: 스켈레톤 표시
    if (viewStore.isLoading) {
        return (
            <div className="flex flex-col gap-4">
                {Array.from({ length: 3 }).map((_, i) => (
                    <LessonItemSkeleton key={i} />
                ))}
            </div>
        );
    }

    // 빈 상태
    if (_.isEmpty(viewStore.userLessons)) {
        return (
            <div className="border-base-200 flex flex-col items-center justify-center rounded-lg border border-dashed py-20">
                <p className="text-base-content/60 text-base">첫 번째 레슨을 시작해보세요</p>
                <p className="text-base-content/40 mt-1 inline-flex items-center text-sm">
                    <MoveUpIcon className="-mx-0.5 size-4" />
                    <MoveUpIcon className="-mx-0.5 size-4" />
                    <MoveUpIcon className="-mx-0.5 size-4" />
                    <span className="mx-2 text-sm">
                        영상 URL로 나만의 레슨을 만들거나, 아래 목록에서 레슨을 선택해 바로 시작할
                        수 있어요
                    </span>
                    <MoveDownIcon className="-mx-0.5 size-4" />
                    <MoveDownIcon className="-mx-0.5 size-4" />
                    <MoveDownIcon className="-mx-0.5 size-4" />
                </p>
            </div>
        );
    }

    // 레슨 목록
    return (
        <div className="flex flex-col gap-4">
            {viewStore.userLessons.map((userLesson) => (
                <LessonItem key={userLesson.lesson.hash} userLesson={userLesson} />
            ))}
            {/* 더보기 버튼 */}
            {!viewStore.isEnd && (
                <button
                    className="btn btn-ghost btn-sm text-base-content/60"
                    onClick={() => viewStore.fetchMore()}
                    disabled={viewStore.isLoadingMore}
                >
                    더보기
                    {viewStore.isLoadingMore ? (
                        <span className="loading loading-ring loading-xs"></span>
                    ) : (
                        <ChevronDownIcon className="size-4" />
                    )}
                </button>
            )}
        </div>
    );
});

export default UserLessonList;
